import { useState } from 'react'
import { supabase } from '../lib/supabase'
import { computeRoundResults, computeMoneyResults } from '../utils/scoringEngine'
import { buildGameHandicapBreakdown } from '../utils/buildGameHandicapBreakdown'
import { buildRoundSnapshot } from '../utils/resultSnapshot'
import { getGameCourse, getGameTee, getMixedTees } from '../constants/courses'

/**
 * Temporary backfill page: recomputes and saves final_results snapshots
 * for completed games that are missing them.
 *
 * Navigate to /backfill to use. Remove after running.
 */
export default function BackfillResults() {
  const [log, setLog] = useState([])
  const [running, setRunning] = useState(false)

  const addLog = (msg) => setLog((prev) => [...prev, msg])

  const run = async () => {
    setRunning(true)
    setLog([])

    try {
      // 1. Find completed games without final_results
      const { data: games, error } = await supabase
        .from('games')
        .select(`
          *,
          game_players (*, player:players (*)),
          scores (*),
          teams (*, team_players (*, player:players (*))),
          bets (*),
          ctp_results (*)
        `)
        .eq('status', 'completed')
        .is('final_results', null)

      if (error) { addLog(`Error fetching games: ${error.message}`); return }
      addLog(`Found ${games.length} completed games without final_results`)

      for (const gameData of games) {
        addLog(`--- Processing game ${gameData.id} (${gameData.game_type}) ---`)

        // 2. Build team structure
        const teams = (gameData.teams || [])
          .sort((a, b) => a.team_order - b.team_order)
          .map((t) => ({
            name: t.team_name,
            playerIds: (t.team_players || []).map((tp) => tp.player_id),
          }))

        if (teams.length < 2) {
          addLog(`  Skipping: only ${teams.length} teams`)
          continue
        }

        // 3. Get match players for handicap data
        const matchId = gameData.match_id
        let matchPlayers = []
        if (matchId) {
          const { data: mp } = await supabase
            .from('match_players')
            .select('*')
            .eq('match_id', matchId)
          matchPlayers = mp || []
        }

        // 4. Build handicap breakdown
        const course = getGameCourse(gameData)
        const tee = getGameTee(gameData)
        const handicapBreakdown = buildGameHandicapBreakdown({
          gameType: gameData.game_type,
          teamStructure: teams,
          matchPlayers,
          gamePlayers: gameData.game_players,
          course,
          tee,
          mixedTees: getMixedTees(gameData),
        })

        addLog(`  Teams: ${teams.map(t => `${t.name} (${t.playerIds.length}p)`).join(' vs ')}`)
        addLog(`  Course: ${course ? 'yes' : 'no'}, Tee: ${tee ? 'yes' : 'no'}, Handicaps: ${handicapBreakdown ? 'yes' : 'no'}`)

        // 5. Compute round results
        const numHoles = gameData.num_holes || 18
        const gameConfig = gameData.game_config || {}
        if (course) gameConfig.holePars = course.par

        let roundResults = null
        try {
          roundResults = computeRoundResults(
            gameData.game_type, teams, gameData.scores || [], handicapBreakdown, numHoles, gameConfig
          )
        } catch (err) {
          addLog(`  Error computing results: ${err.message}`)
          continue
        }

        if (!roundResults) {
          addLog(`  Skipping: no round results computed`)
          continue
        }

        addLog(`  Format: ${roundResults.format}, Type: ${roundResults.type}`)

        // Log segment winners
        const formatKey = roundResults.format // 'nassau', 'stableford', 'six_pack', 'per_nine'
        const segData = roundResults[formatKey]
        if (segData) {
          if (formatKey === 'per_nine' && roundResults.nineResults) {
            for (const nine of roundResults.nineResults) {
              addLog(`  ${nine.label}: winner=${nine.winner}`)
            }
          } else {
            for (const seg of ['front', 'back', 'overall']) {
              if (segData[seg]) {
                addLog(`  ${seg}: winner=${segData[seg].winner}, complete=${segData[seg].complete}`)
              }
            }
          }
        }

        // 6. Compute money results
        const pressDataArr = (gameData.bets || [])
          .filter((b) => b.bet_type === 'press')
          .map((p) => ({
            segment: p.metadata?.segment,
            startHole: p.hole_start,
            wager: Number(p.amount),
            pressing_team: p.metadata?.pressing_team,
          }))

        let moneyResults = []
        if (gameData.wager > 0) {
          try {
            moneyResults = computeMoneyResults(roundResults, teams, Number(gameData.wager), gameData.game_type, pressDataArr, { nassauWeights: gameData.game_config?.nassauWeights })
          } catch (err) {
            addLog(`  Warning: money results failed: ${err.message}`)
          }
        }

        // 7. Build and save snapshot
        const snapshot = buildRoundSnapshot({
          roundResults,
          moneyResults,
          handicapBreakdown,
          teamStructure: teams,
          sideBets: {},
          pressData: pressDataArr,
        })

        const { error: updateError } = await supabase
          .from('games')
          .update({ final_results: snapshot })
          .eq('id', gameData.id)

        if (updateError) {
          addLog(`  ERROR saving: ${updateError.message}`)
        } else {
          addLog(`  Saved final_results successfully!`)
        }
      }

      addLog('=== Done ===')
    } catch (err) {
      addLog(`Unexpected error: ${err.message}`)
    } finally {
      setRunning(false)
    }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: '800px', margin: '0 auto' }}>
      <h2 style={{ marginBottom: '1rem' }}>Backfill Final Results</h2>
      <p style={{ color: 'var(--text-muted)', marginBottom: '1rem' }}>
        Recomputes and saves final_results snapshots for completed games that are missing them.
      </p>
      <button
        onClick={run}
        disabled={running}
        style={{
          padding: '0.5rem 1.5rem',
          backgroundColor: 'var(--accent)',
          color: 'white',
          border: 'none',
          borderRadius: '9999px',
          cursor: running ? 'not-allowed' : 'pointer',
          marginBottom: '1rem',
        }}
      >
        {running ? 'Running...' : 'Run Backfill'}
      </button>
      <div style={{
        backgroundColor: 'var(--bg-secondary)',
        border: '1px solid var(--border-color)',
        borderRadius: '0.5rem',
        padding: '1rem',
        fontFamily: 'monospace',
        fontSize: '0.8125rem',
        whiteSpace: 'pre-wrap',
        maxHeight: '60vh',
        overflowY: 'auto',
      }}>
        {log.length === 0 ? 'Click "Run Backfill" to start...' : log.join('\n')}
      </div>
    </div>
  )
}
